import { tx } from "@/i18n/tx";
import { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtCHF, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import DuplicateDialog from "@/components/invoices/DuplicateDialog";
import { Pencil, Sparkles, X, Mic, MicOff, RotateCcw, Zap } from "lucide-react";
import SaleHistory from "@/components/caisse/SaleHistory";

const TVA_SP = 8.1, TVA_EM = 2.6;
const today = () => new Date().toISOString().slice(0, 10);

export default function Caisse() {
  const [sales, setSales] = useState([]);
  const [date, setDate] = useState(today());
  const [sp, setSp] = useState("");
  const [em, setEm] = useState("");
  const [nb, setNb] = useState("");
  const [cartes, setCartes] = useState("");
  const [twint, setTwint] = useState("");
  const [cashCompte, setCashCompte] = useState("");
  const [fondCaisse, setFondCaisse] = useState("");
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(null);
  const [listening, setListening] = useState(false);
  const [quickMode, setQuickMode] = useState(false); // Fermeture rapide mode
  const [quickTotal, setQuickTotal] = useState("");
  const [quickCartes, setQuickCartes] = useState("");
  const [quickTwint, setQuickTwint] = useState("");
  const [quickFond, setQuickFond] = useState("");
  const [quickCashCounted, setQuickCashCounted] = useState("");
  const [quickSplit, setQuickSplit] = useState(100); // % sur place (rest = à emporter)
  const recognitionRef = useRef(null);
  const submitRef = useRef(null);

  const applyYesterday = async () => {
    try {
      const { data } = await api.get("/sales/latest");
      if (!data) { toast.info(tx("ui_caisse.k864061")); return; }
      setSp(String(data.ca_sur_place || ""));
      setEm(String(data.ca_emporter || ""));
      setNb(String(data.nb_couverts || ""));
      setCartes(String(data.montant_cartes || ""));
      setTwint(String(data.montant_twint || ""));
      if (data.fond_caisse != null) setFondCaisse(String(data.fond_caisse));
      // Don't copy cash_compte — that varies daily
      toast.success(tx("ui_caisse.k7599ab"));
    } catch {
      toast.error(tx("ui_caisse.k717585"));
    }
  };

  // Fermeture rapide: parse CA, cards, twint, float, counted cash → auto-fill everything
  const quickTotalNum = parseFloat(quickTotal) || 0;
  const quickCartesNum = parseFloat(quickCartes) || 0;
  const quickTwintNum = parseFloat(quickTwint) || 0;
  const quickFondNum = parseFloat(quickFond) || 0;
  const quickCashCountedNum = parseFloat(quickCashCounted) || 0;
  const cashAttenduVente = Math.max(0, +(quickTotalNum - quickCartesNum - quickTwintNum).toFixed(2));
  const bourseAttendue = +(quickFondNum + cashAttenduVente).toFixed(2);
  const pourboireAuto = quickCashCounted ? +(quickCashCountedNum - bourseAttendue).toFixed(2) : null;

  const applyQuickTotal = () => {
    if (quickTotalNum <= 0) { toast.error(tx("ui_caisse.k977997")); return; }
    const spVal = +(quickTotalNum * quickSplit / 100).toFixed(2);
    const emVal = +(quickTotalNum - spVal).toFixed(2);
    setSp(String(spVal));
    setEm(String(emVal));
    setCartes(quickCartes ? String(quickCartesNum) : "");
    setTwint(quickTwint ? String(quickTwintNum) : "");
    setFondCaisse(quickFond ? String(quickFondNum) : "");
    setCashCompte(quickCashCounted ? String(quickCashCountedNum) : "");
    setQuickMode(false);
    const tipMsg = pourboireAuto != null
      ? (pourboireAuto >= 0 ? ` · Pourboire : ${fmtCHF(pourboireAuto)}` : ` · ⚠️ Manquant : ${fmtCHF(Math.abs(pourboireAuto))}`)
      : "";
    toast.success(`${tx("tpl.split", { sp: fmtCHF(spVal), em: fmtCHF(emVal) })}${tipMsg}`);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error(tx("ui_caisse.keb652d"));
      return;
    }
    const rec = new SR();
    rec.lang = "fr-CH";
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    rec.onstart = () => setListening(true);
    rec.onerror = (e) => { setListening(false); if (e.error !== "no-speech") toast.error(tx("ui_caisse.kfc3939") + e.error); };
    rec.onend = () => setListening(false);
    rec.onresult = async (evt) => {
      const text = evt.results[0][0].transcript;
      toast.info(`« ${text} »`);
      try {
        const { data } = await api.post("/sales/parse-voice", { text }, { timeout: 20000 });
        if (data.ca_sur_place > 0) setSp(String(data.ca_sur_place));
        if (data.ca_emporter > 0) setEm(String(data.ca_emporter));
        if (data.nb_couverts > 0) setNb(String(data.nb_couverts));
        toast.success(tx("ui_caisse.k7ccf18"));
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    };
    rec.start();
  };

  const stopVoice = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/sales");
    setSales(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const spN = parseFloat(sp) || 0;
  const emN = parseFloat(em) || 0;
  const cartesN = parseFloat(cartes) || 0;
  const twintN = parseFloat(twint) || 0;
  const cashCompteN = cashCompte === "" ? null : parseFloat(cashCompte);
  const totalTtc = spN + emN;
  const tvaSp = +(spN * TVA_SP / (100 + TVA_SP)).toFixed(2);
  const tvaEm = +(emN * TVA_EM / (100 + TVA_EM)).toFixed(2);
  const tvaTotal = tvaSp + tvaEm;
  const ticket = nb ? +(totalTtc / parseFloat(nb)).toFixed(2) : 0;
  const cashAttendu = +(totalTtc - cartesN - twintN).toFixed(2);
  const ecartCash = cashCompteN != null && !Number.isNaN(cashCompteN) ? +(cashCompteN - cashAttendu).toFixed(2) : null;

  // Mobile keyboard bringing button into view
  const onInputFocus = (e) => {
    setTimeout(() => {
      e.target.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 250);
  };

  const [dupSale, setDupSale] = useState(null); // {existing_id, date}
  const [editingId, setEditingId] = useState(null);
  const [reason, setReason] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!spN && !emN) { toast.error(tx("ui_caisse.ke680c7")); return; }
    if (editingId && reason.trim().length < 3) { toast.error(tx("ui_caisse.k236550")); return; }
    const fondN = fondCaisse !== "" ? parseFloat(fondCaisse) : null;
    const payload = {
        date,
        ca_sur_place: spN,
        ca_emporter: emN,
        nb_couverts: parseInt(nb) || 0,
        montant_cartes: cartesN,
        montant_twint: twintN,
        cash_compte: cashCompteN != null && !Number.isNaN(cashCompteN) ? cashCompteN : null,
        fond_caisse: fondN != null && !Number.isNaN(fondN) ? fondN : null,
        notes: "",
      };
    await saveSale(payload, editingId);
  };

  const openExisting = async (day) => {
    const { data } = await api.get(`/sales/by-date/${day}`);
    if (!data) return;
    const v = (x) => (x == null || x === 0 ? "" : String(x));
    setQuickMode(false);
    setDate(data.date); setSp(v(data.ca_sur_place)); setEm(v(data.ca_emporter)); setNb(v(data.nb_couverts));
    setCartes(v(data.montant_cartes)); setTwint(v(data.montant_twint)); setCashCompte(v(data.cash_compte)); setFondCaisse(v(data.fond_caisse));
    setEditingId(data.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveSale = async (payload, existingId) => {
    setLoading(true);
    try {
      if (existingId) await api.put(`/sales/${existingId}`, { ...payload, reason: reason.trim() });
      else await api.post("/sales", payload);
      toast.success(existingId ? tx("ui_caisse.k82aa55") : tx("ui_caisse.k64d2d1"));
      setEditingId(null); setReason("");
      setSp(""); setEm(""); setNb(""); setCartes(""); setTwint(""); setCashCompte(""); setFondCaisse("");
      await load();
      // Fetch AI tip after save (non-blocking)
      api.get("/sales/tip").then((r) => setTip(r.data)).catch(() => {});
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409) setDupSale({ existing_id: d?.existing_id, date: d?.date || payload.date });
      else toast.error(formatApiErrorDetail(d));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">{tx("ui_caisse.k111b80")} <span className="font-serif-italic text-primary-c">{tx("ui_caisse.k942a32")}</span></h1>
          <p className="text-text-secondary text-sm mt-1">{tx("ui_caisse.k509568")}</p>
        </div>
        <button
          type="button"
          onClick={listening ? stopVoice : startVoice}
          data-testid="caisse-voice-button"
          className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-transform shrink-0 ${listening ? "bg-red-500 text-white animate-pulse" : "bg-secondary-c text-white"}`}
          title={listening ? tx("ui_caisse.k0c237d") : tx("ui_caisse.k9d928f")}
          aria-label={tx("ui_caisse.k72da33")}
        >
          {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
      </div>
      {listening && (
        <p className="text-xs text-red-600 font-semibold text-center animate-pulse" data-testid="caisse-voice-status">
          {tx("ui_caisse.k7f0c2a")}
        </p>
      )}

      {/* Quick actions row */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={applyYesterday}
          data-testid="caisse-reuse-latest"
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-2xl bg-surface-muted border border-c text-sm font-semibold hover:bg-c/40 transition-colors"
        >
          <RotateCcw className="w-4 h-4 text-primary-c" /> {tx("ui_caisse.k16db8e")}
        </button>
        <button
          type="button"
          onClick={() => setQuickMode((v) => !v)}
          data-testid="caisse-quick-mode-toggle"
          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-semibold transition-colors ${quickMode ? "bg-secondary-c text-white" : "bg-surface-muted border border-c hover:bg-c/40"}`}
        >
          <Zap className="w-4 h-4" /> {tx("ui_caisse.kcc2abd")}
        </button>
      </div>

      {/* Fermeture rapide panel */}
      {quickMode && (
        <div className="card-c bg-secondary-c/5 border-secondary-c/30 space-y-3" data-testid="caisse-quick-panel">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-secondary-c">{tx("ui_caisse.kdbd1da")}</p>
            <p className="text-[11px] text-text-secondary mt-0.5">{tx("ui_caisse.kefa56d")}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.k0a1a6f")}</label>
            <div className="relative">
              <input inputMode="decimal" value={quickTotal} onChange={(e) => setQuickTotal(e.target.value.replace(",", "."))} data-testid="caisse-quick-total-input" className="input-c pr-14 text-xl font-display" placeholder="0.00" autoFocus />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary text-xs">CHF</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">{tx("r3.cdc")}</label>
              <input inputMode="decimal" value={quickCartes} onChange={(e) => setQuickCartes(e.target.value.replace(",", "."))} data-testid="caisse-quick-cartes" className="input-c" placeholder="0.00" />
            </div>
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">TWINT</label>
              <input inputMode="decimal" value={quickTwint} onChange={(e) => setQuickTwint(e.target.value.replace(",", "."))} data-testid="caisse-quick-twint" className="input-c" placeholder="0.00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.k0fb81e")}</label>
              <input inputMode="decimal" value={quickFond} onChange={(e) => setQuickFond(e.target.value.replace(",", "."))} data-testid="caisse-quick-fond" className="input-c" placeholder="200.00" />
            </div>
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.ke01144")}</label>
              <input inputMode="decimal" value={quickCashCounted} onChange={(e) => setQuickCashCounted(e.target.value.replace(",", "."))} data-testid="caisse-quick-cash-counted" className="input-c" placeholder="0.00" />
            </div>
          </div>

          {/* Live summary */}
          {quickTotalNum > 0 && (
            <div className="p-3 rounded-2xl bg-white border border-c space-y-1.5 text-xs" data-testid="caisse-quick-summary">
              <div className="flex justify-between">
                <span className="text-text-secondary">{tx("ui_caisse.k065102")}</span>
                <span className="font-semibold">{fmtCHF(cashAttenduVente)}</span>
              </div>
              {quickFond && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">{tx("ui_caisse.k3a5f7b")}</span>
                  <span className="font-semibold">{fmtCHF(quickFondNum)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-c pt-1.5">
                <span className="font-semibold">{tx("r3.expected_purse")}</span>
                <span className="font-display text-primary-c">{fmtCHF(bourseAttendue)}</span>
              </div>
              {pourboireAuto != null && (
                <div className={`flex justify-between pt-1.5 border-t border-c ${pourboireAuto >= 0 ? "text-primary-c" : "text-red-600"}`} data-testid="caisse-quick-pourboire">
                  <span className="font-semibold">{pourboireAuto >= 0 ? "💸 Pourboire" : "⚠️ Manquant"}</span>
                  <span className="font-display">{fmtCHF(Math.abs(pourboireAuto))}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <div className="flex justify-between text-[10px] text-text-secondary mb-1">
              <span>Sur place {quickSplit}%</span>
              <span>À emporter {100 - quickSplit}%</span>
            </div>
            <input type="range" min="0" max="100" step="5" value={quickSplit} onChange={(e) => setQuickSplit(parseInt(e.target.value))} data-testid="caisse-quick-split-slider" className="w-full accent-primary-c" />
          </div>

          <button type="button" onClick={applyQuickTotal} data-testid="caisse-quick-apply" className="btn-primary w-full">
            {tx("ui_caisse.kc04b36")}
          </button>
        </div>
      )}

      {editingId && (
        <div className="card-c !p-3 flex items-center gap-3 border-2 border-secondary-c" data-testid="caisse-editing-banner">
          <p className="text-sm flex-1">{tx("ui_caisse.k599611")} <strong>{fmtDate(date)}</strong> {tx("ui_caisse.k75057e")}</p>
          <button type="button" onClick={() => { setEditingId(null); setReason(""); setDate(today()); setSp(""); setEm(""); setNb(""); setCartes(""); setTwint(""); setCashCompte(""); setFondCaisse(""); }} className="btn-secondary !py-2 text-sm" data-testid="caisse-editing-cancel">{tx("ui_caisse.kd12c3b")}</button>
        </div>
      )}
      <form onSubmit={onSubmit} className="card-c space-y-4">
        {editingId && (
          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5">{tx("ui_caisse.kbdd697")}</label>
            <input data-testid="caisse-correction-reason" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} className="input-c" placeholder={tx("ui_caisse.k962650")} />
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-text-secondary block mb-1.5">{tx("ui_caisse.k447497")}</label>
          <input data-testid="caisse-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-c" />
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5">
              {tx("ui_caisse.k2e50b7")} <span className="text-text-secondary font-normal">{tx("r3.ttc")}</span>
            </label>
            <div className="relative">
              <input
                data-testid="caisse-sp-input"
                inputMode="decimal"
                value={sp}
                onFocus={onInputFocus}
                onChange={(e) => setSp(e.target.value.replace(",", "."))}
                className="input-c pr-14 text-xl font-display"
                placeholder="0.00"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary text-sm">CHF</span>
            </div>
            <p className="text-xs text-text-secondary mt-1.5">TVA {TVA_SP}% = <span className="font-semibold text-[var(--text)]">{fmtCHF(tvaSp)}</span></p>
          </div>

          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5">
              À emporter <span className="text-text-secondary font-normal">{tx("r3.ttc")}</span>
            </label>
            <div className="relative">
              <input
                data-testid="caisse-em-input"
                inputMode="decimal"
                value={em}
                onFocus={onInputFocus}
                onChange={(e) => setEm(e.target.value.replace(",", "."))}
                className="input-c pr-14 text-xl font-display"
                placeholder="0.00"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary text-sm">CHF</span>
            </div>
            <p className="text-xs text-text-secondary mt-1.5">TVA {TVA_EM}% = <span className="font-semibold text-[var(--text)]">{fmtCHF(tvaEm)}</span></p>
          </div>

          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5">{tx("ui_caisse.kdbb92e")} <span className="text-text-secondary font-normal">{tx("r3.optional")}</span></label>
            <input data-testid="caisse-nb-input" inputMode="numeric" value={nb} onFocus={onInputFocus} onChange={(e) => setNb(e.target.value)} className="input-c" placeholder="0" />
          </div>

          {/* Paiements électroniques */}
          <div className="pt-2 border-t border-c">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary mb-2">{tx("ui_caisse.k71b6c2")}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.k7084d3")}</label>
                <div className="relative">
                  <input
                    data-testid="caisse-cartes-input"
                    inputMode="decimal"
                    value={cartes}
                    onFocus={onInputFocus}
                    onChange={(e) => setCartes(e.target.value.replace(",", "."))}
                    className="input-c pr-12 text-base"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-xs">CHF</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.kd69554")}</label>
                <div className="relative">
                  <input
                    data-testid="caisse-twint-input"
                    inputMode="decimal"
                    value={twint}
                    onFocus={onInputFocus}
                    onChange={(e) => setTwint(e.target.value.replace(",", "."))}
                    className="input-c pr-12 text-base"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-xs">CHF</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bourse - cash attendu */}
          <div className="rounded-2xl p-3.5 bg-[#EFF6F1] border border-primary-c/20" data-testid="caisse-cash-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-primary-c">{tx("ui_caisse.k2a67bb")}</p>
              <p className="font-display text-2xl text-primary-c" data-testid="caisse-cash-attendu">{fmtCHF(cashAttendu)}</p>
            </div>
            <p className="text-[11px] text-text-secondary">Total {fmtCHF(totalTtc)} − Cartes {fmtCHF(cartesN)} − TWINT {fmtCHF(twintN)}</p>
            <div className="mt-3">
              <label className="text-xs font-medium text-text-secondary block mb-1">{tx("ui_caisse.k06f583")} <span className="text-text-secondary font-normal">{tx("r3.optional")}</span></label>
              <div className="relative">
                <input
                  data-testid="caisse-cash-compte-input"
                  inputMode="decimal"
                  value={cashCompte}
                  onFocus={onInputFocus}
                  onChange={(e) => setCashCompte(e.target.value.replace(",", "."))}
                  className="input-c pr-12 text-base"
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-xs">CHF</span>
              </div>
              {ecartCash !== null && !Number.isNaN(ecartCash) && (
                <p
                  data-testid="caisse-ecart"
                  className={`text-xs mt-1.5 font-semibold ${ecartCash === 0 ? "text-primary-c" : ecartCash > 0 ? "text-secondary-c" : "text-red-600"}`}
                >
                  {ecartCash === 0 ? tx("ui_caisse.k68541d") : ecartCash > 0 ? `Surplus : +${fmtCHF(ecartCash)}` : `Manquant : ${fmtCHF(ecartCash)}`}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-surface-muted rounded-2xl p-3 sm:p-4 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[9px] sm:text-[10px] text-text-secondary uppercase tracking-wider font-semibold">{tx("ui_caisse.k96b014")}</p>
            <p className="font-display text-base sm:text-lg mt-1 leading-tight break-words" data-testid="caisse-total">{fmtCHF(totalTtc)}</p>
          </div>
          <div className="border-x border-c">
            <p className="text-[9px] sm:text-[10px] text-text-secondary uppercase tracking-wider font-semibold">TVA</p>
            <p className="font-display text-base sm:text-lg mt-1 leading-tight break-words">{fmtCHF(tvaTotal)}</p>
          </div>
          <div>
            <p className="text-[9px] sm:text-[10px] text-text-secondary uppercase tracking-wider font-semibold">{tx("ui_caisse.kc75143")}</p>
            <p className="font-display text-base sm:text-lg mt-1 leading-tight break-words">{fmtCHF(ticket)}</p>
          </div>
        </div>

        <button ref={submitRef} data-testid="caisse-submit-button" disabled={loading} className="btn-primary w-full">
          {loading ? "Enregistrement…" : editingId ? tx("ui_caisse.k6b1332") : tx("ui_caisse.kf445a9")}
        </button>
      </form>

      {tip && (
        <div
          data-testid="caisse-tip-card"
          className={`card-c relative border-l-4 ${
            tip.tone === "positive" ? "border-l-primary-c bg-gradient-to-br from-white to-[#EFF6F1]" :
            tip.tone === "warning" ? "border-l-red-400 bg-gradient-to-br from-white to-red-50" :
            "border-l-secondary-c bg-gradient-to-br from-white to-[#FBF4E1]"
          }`}
        >
          <button
            onClick={() => setTip(null)}
            className="absolute top-2 right-2 text-text-secondary hover:text-[var(--text)] p-1"
            data-testid="caisse-tip-close"
            aria-label={tx("ui_caisse.k66a3cc")}
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
              tip.tone === "positive" ? "bg-primary-c" :
              tip.tone === "warning" ? "bg-red-400" :
              "bg-secondary-c"
            }`}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">{tx("ui_caisse.kdb70cf")}</p>
              <p className="text-sm mt-1 leading-relaxed">{tip.insight}</p>
              {tip.avg_14d > 0 && (
                <p className="text-[11px] text-text-secondary mt-2">
                  Moyenne 14j : {fmtCHF(tip.avg_14d)} · Aujourd'hui : <strong className="text-[var(--text)]">{fmtCHF(tip.today_ttc)}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-display text-xl mb-3">{tx("ui_caisse.k35723f")}</h3>
        {sales.length === 0 ? (
          <p className="text-text-secondary text-sm text-center py-6">{tx("ui_caisse.kfe7458")}</p>
        ) : (
          <ul className="space-y-2">
            {sales.map((s) => (
              <li key={s.id} className="card-c space-y-2" data-testid={`sale-row-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1 pr-3">
                    <p className="font-semibold flex items-center gap-2">{fmtDate(s.date)}
                      {s.corrected && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#FFF8EB] text-[#B45309] border border-[#F5C87A]" data-testid={`sale-corrected-${s.id}`}>{tx("ui_caisse.k0463b7")}</span>}
                    </p>
                    <p className="text-xs text-text-secondary truncate">{fmtCHF(s.ca_sur_place)} sur place · {fmtCHF(s.ca_emporter)} à emporter</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <p className="font-display text-lg">{fmtCHF(s.total_ttc)}</p>
                    <button onClick={() => openExisting(s.date)} className="text-text-secondary hover:text-primary-c p-1" data-testid={`sale-edit-${s.id}`} aria-label={tx("ui_caisse.kb0b168")}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {s.corrected && <SaleHistory saleId={s.id} />}
              </li>
            ))}
          </ul>
        )}
      </div>
      <DuplicateDialog
        testId="sale-duplicate-dialog"
        open={dupSale}
        title={tx("ui_caisse.k3d6181")}
        description={dupSale && <p>Une caisse existe déjà pour le {fmtDate(dupSale.date)}. Une seule caisse par jour est possible : ouvrez-la pour la corriger.</p>}
        confirmLabel={tx("ui_caisse.k968509")}
        cancelLabel={tx("ui_caisse.k7d2062")}
        onCancel={() => setDupSale(null)}
        onConfirm={() => { const d = dupSale; setDupSale(null); openExisting(d.date); }}
      />
    </div>
  );
}
